import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { getLegalPage } from '../utils/api';

const PrivacyStatement = () => {
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const res = await getLegalPage('privacy_policy');
        const data = res.data || {};
        // Only use CMS content if it has actual content
        if (data.content && data.content.trim()) {
          setCmsContent(data);
        }
      } catch (error) {
        console.error('Failed to load Privacy Statement:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818]"></div>
      </div>
    );
  }

  // If CMS content exists, render it
  if (cmsContent && cmsContent.content) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="py-12 bg-[#F9F9F7]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {cmsContent.title || 'Privacy Statement'}
            </h1>
            {cmsContent.last_updated_date && (
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Last updated: {cmsContent.last_updated_date}
              </p>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: cmsContent.content }} />
          </div>
        </div>
      </div>
    );
  }

  // Default static content (original page)
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Privacy Statement
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Last updated: December 8, 2025
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="prose prose-slate max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
              <p className="text-slate-700">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, and password when you register</li>
                <li><strong>Booking Information:</strong> Viber contact number, consultation details, and payment proof</li>
                <li><strong>Communication Data:</strong> Messages sent through our forum or contact forms</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-700">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process consultation bookings and payments</li>
                <li>Send booking confirmations and service-related communications</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Ensure the security of our platform</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Information Sharing</h2>
              <p className="text-slate-700">
                We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
                <li>With service providers who assist in our operations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Data Security</h2>
              <p className="text-slate-700">
                We implement appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Retention</h2>
              <p className="text-slate-700">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy statement, unless a longer retention period is required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Rights</h2>
              <p className="text-slate-700">You have the right to:</p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Withdraw consent for data processing</li>
                <li>Lodge a complaint with a supervisory authority</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Cookies</h2>
              <p className="text-slate-700">
                We use cookies and similar technologies to enhance your experience, analyze usage patterns, and personalize content. You can control cookie settings through your browser.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Changes to This Statement</h2>
              <p className="text-slate-700">
                We may update this privacy statement from time to time. We will notify you of any changes by posting the new statement on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Contact Us</h2>
              <p className="text-slate-700">
                If you have questions about this Privacy Statement, please contact us at <a href="mailto:admin@lexdoright.com" className="text-[#8B1818] hover:underline">admin@lexdoright.com</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyStatement;
